import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Users, FileText, Shield, Lock, Activity, Mail,
  Plus, Pencil, Trash2, Loader2, ChevronLeft, ChevronRight,
  Download, Upload, AlertTriangle, CheckCircle2, XCircle,
  Search, RefreshCw, Eye
} from 'lucide-react'
import api from '../services/api'
import { useLanguage } from '../hooks/useLanguage'
import { useWorkspace } from '../hooks/useWorkspace'

// ============================================================
// TYPES
// ============================================================

interface Soggetto {
  id: string
  codice_fiscale: string
  denominazione: string
  cognome: string
  nome: string
  sesso: string
  comune_sede: string
  provincia_sede: string
  cap_sede: string
  indirizzo_sede: string
  data_nascita: string
  comune_nascita: string
  provincia_nascita: string
  sid_number: string
  tipo_soggetto: string
  stato_accreditamento: string
  pec_address: string
  flag_cessazione: number
  created_at: string
  updated_at: string
}

interface Ricevuta {
  id: string
  comunicazione_id: string
  tipo_ricevuta: string
  esito: string
  protocollo_ade: string
  errori: { codice: string; messaggio: string }[]
  filename: string
  created_at: string
}

interface AuditEvent {
  id: number
  action: string
  target_type: string
  target_id: string
  actor_email: string
  detail: Record<string, unknown> | null
  ip_address: string
  created_at: string
}

interface SidStatus {
  certificato: { presente: boolean; ambiente?: string; filename?: string; attivo?: boolean; caricato_il?: string }
  soggetti: { totale: number }
  comunicazioni: { totale: number; per_stato: Record<string, number> }
  ricevute: { totale: number; scartate: number }
  ultimi_eventi: { azione: string; tipo: string; target: string; data: string }[]
}

interface PecLookupResult {
  found: boolean
  pec?: string
  company_name?: string
  error?: string
}

type Tab = 'soggetti' | 'ricevute' | 'audit' | 'privacy' | 'status' | 'pec'

const TABS: { key: Tab; icon: typeof Users; labelIt: string; labelEn: string }[] = [
  { key: 'status', icon: Activity, labelIt: 'Status', labelEn: 'Status' },
  { key: 'soggetti', icon: Users, labelIt: 'Soggetti', labelEn: 'Subjects' },
  { key: 'ricevute', icon: FileText, labelIt: 'Ricevute', labelEn: 'Receipts' },
  { key: 'audit', icon: Shield, labelIt: 'Audit', labelEn: 'Audit' },
  { key: 'privacy', icon: Lock, labelIt: 'Privacy', labelEn: 'Privacy' },
  { key: 'pec', icon: Mail, labelIt: 'PEC', labelEn: 'PEC' },
]

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function SidAdmin() {
  const { language } = useLanguage()
  useWorkspace() // ensure workspace context is active
  const isIt = language === 'it'
  const [tab, setTab] = useState<Tab>('status')

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-indigo-100 dark:bg-indigo-900/30">
          <Shield className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {isIt ? 'Amministrazione SID' : 'SID Administration'}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {isIt ? 'Gestione soggetti, ricevute, audit, privacy e configurazione PEC' : 'Manage subjects, receipts, audit, privacy and PEC configuration'}
          </p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-100 dark:bg-slate-800 rounded-xl p-1 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon
          const active = tab === t.key
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                active
                  ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {isIt ? t.labelIt : t.labelEn}
            </button>
          )
        })}
      </div>

      {/* Tab content */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
        {tab === 'status' && <StatusSection isIt={isIt} />}
        {tab === 'soggetti' && <SoggettiSection isIt={isIt} />}
        {tab === 'ricevute' && <RicevuteSection isIt={isIt} />}
        {tab === 'audit' && <AuditSection isIt={isIt} />}
        {tab === 'privacy' && <PrivacySection isIt={isIt} />}
        {tab === 'pec' && <PecSection isIt={isIt} />}
      </div>
    </div>
  )
}

// ============================================================
// STATUS SECTION
// ============================================================

function StatusSection({ isIt }: { isIt: boolean }) {
  const { data: status, isLoading } = useQuery<SidStatus>({
    queryKey: ['sid-status'],
    queryFn: async () => {
      const r = await api.get('/sid/status')
      return r.data.data
    },
  })

  if (isLoading) return <LoadingSpinner />

  if (!status) return <EmptyState message={isIt ? 'Impossibile caricare lo stato' : 'Unable to load status'} />

  return (
    <div className="p-5 space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard
          label={isIt ? 'Certificato' : 'Certificate'}
          value={status.certificato.presente ? (status.certificato.attivo ? '✅ ' + (isIt ? 'Attivo' : 'Active') : '⚠️ ' + (isIt ? 'Non attivo' : 'Inactive')) : '❌ ' + (isIt ? 'Mancante' : 'Missing')}
          sub={status.certificato.filename || ''}
        />
        <SummaryCard
          label={isIt ? 'Soggetti' : 'Subjects'}
          value={String(status.soggetti.totale)}
          sub={isIt ? 'registrati' : 'registered'}
        />
        <SummaryCard
          label={isIt ? 'Comunicazioni' : 'Communications'}
          value={String(status.comunicazioni.totale)}
          sub={Object.entries(status.comunicazioni.per_stato).map(([k, v]) => `${k}: ${v}`).join(', ') || '—'}
        />
        <SummaryCard
          label={isIt ? 'Ricevute' : 'Receipts'}
          value={String(status.ricevute.totale)}
          sub={status.ricevute.scartate > 0 ? `${status.ricevute.scartate} ${isIt ? 'scartate' : 'rejected'}` : '—'}
        />
      </div>

      {/* Recent events */}
      {status.ultimi_eventi.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {isIt ? 'Ultimi eventi' : 'Recent events'}
          </h3>
          <div className="space-y-1">
            {status.ultimi_eventi.map((e, i) => (
              <div key={i} className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 py-1 border-b border-gray-100 dark:border-slate-700 last:border-0">
                <span className="font-mono">{e.azione}</span>
                <span>{e.tipo} / {e.target?.slice(0, 16)}</span>
                <span>{new Date(e.data).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function SummaryCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="p-3 rounded-lg bg-gray-50 dark:bg-slate-900/50 border border-gray-100 dark:border-slate-700">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-lg font-bold text-gray-900 dark:text-white mt-0.5">{value}</p>
      <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">{sub}</p>
    </div>
  )
}

// ============================================================
// SOGGETTI SECTION
// ============================================================

const EMPTY_SOGGETTO = {
  codice_fiscale: '', denominazione: '', cognome: '', nome: '', sesso: '',
  comune_sede: '', provincia_sede: '', cap_sede: '', indirizzo_sede: '',
  data_nascita: '', comune_nascita: '', provincia_nascita: '',
  sid_number: '', tipo_soggetto: 'diretto', stato_accreditamento: 'non_verificato',
  pec_address: '', flag_cessazione: false,
}

function SoggettiSection({ isIt }: { isIt: boolean }) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'list' | 'create' | 'edit'>('list')
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState({ ...EMPTY_SOGGETTO })
  const [formError, setFormError] = useState<string | null>(null)

  const { data: soggetti = [], isLoading } = useQuery<Soggetto[]>({
    queryKey: ['sid-soggetti'],
    queryFn: async () => {
      const r = await api.get('/sid/soggetti')
      return r.data.data || []
    },
  })

  const createMutation = useMutation({
    mutationFn: async (data: typeof EMPTY_SOGGETTO) => {
      const r = await api.post('/sid/soggetti', data)
      return r.data
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['sid-soggetti'] })
        setMode('list')
        setForm({ ...EMPTY_SOGGETTO })
        setFormError(null)
      } else {
        setFormError(data.error || 'Errore')
      }
    },
    onError: (err: unknown) => setFormError(err instanceof Error ? err.message : 'Errore'),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof EMPTY_SOGGETTO }) => {
      const r = await api.put(`/sid/soggetti/${id}`, data)
      return r.data
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['sid-soggetti'] })
        setMode('list')
        setEditId(null)
        setForm({ ...EMPTY_SOGGETTO })
        setFormError(null)
      } else {
        setFormError(data.error || 'Errore')
      }
    },
    onError: (err: unknown) => setFormError(err instanceof Error ? err.message : 'Errore'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.delete(`/sid/soggetti/${id}`)
      return r.data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sid-soggetti'] }),
  })

  const startEdit = (s: Soggetto) => {
    setEditId(s.id)
    setForm({
      codice_fiscale: s.codice_fiscale || '',
      denominazione: s.denominazione || '',
      cognome: s.cognome || '',
      nome: s.nome || '',
      sesso: s.sesso || '',
      comune_sede: s.comune_sede || '',
      provincia_sede: s.provincia_sede || '',
      cap_sede: s.cap_sede || '',
      indirizzo_sede: s.indirizzo_sede || '',
      data_nascita: s.data_nascita || '',
      comune_nascita: s.comune_nascita || '',
      provincia_nascita: s.provincia_nascita || '',
      sid_number: s.sid_number || '',
      tipo_soggetto: s.tipo_soggetto || 'diretto',
      stato_accreditamento: s.stato_accreditamento || 'non_verificato',
      pec_address: s.pec_address || '',
      flag_cessazione: !!s.flag_cessazione,
    })
    setFormError(null)
    setMode('edit')
  }

  const handleSubmit = () => {
    setFormError(null)
    if (!form.codice_fiscale.trim()) {
      setFormError(isIt ? 'Codice fiscale obbligatorio' : 'Tax code required')
      return
    }
    if (mode === 'edit' && editId) {
      updateMutation.mutate({ id: editId, data: form })
    } else {
      createMutation.mutate(form)
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending

  // ── FORM VIEW ──
  if (mode === 'create' || mode === 'edit') {
    return (
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {mode === 'create' ? (isIt ? 'Nuovo soggetto' : 'New subject') : (isIt ? 'Modifica soggetto' : 'Edit subject')}
          </h3>
          <button onClick={() => { setMode('list'); setFormError(null) }} className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            {isIt ? '← Torna alla lista' : '← Back to list'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField label={isIt ? 'Codice Fiscale *' : 'Tax Code *'} value={form.codice_fiscale} onChange={v => setForm(f => ({ ...f, codice_fiscale: v.toUpperCase() }))} maxLength={16} mono />
          <FormField label={isIt ? 'Denominazione' : 'Company Name'} value={form.denominazione} onChange={v => setForm(f => ({ ...f, denominazione: v }))} />
          <FormField label={isIt ? 'Cognome' : 'Surname'} value={form.cognome} onChange={v => setForm(f => ({ ...f, cognome: v }))} />
          <FormField label={isIt ? 'Nome' : 'First Name'} value={form.nome} onChange={v => setForm(f => ({ ...f, nome: v }))} />
          <div>
            <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Tipo Soggetto' : 'Subject Type'}</label>
            <select value={form.tipo_soggetto} onChange={e => setForm(f => ({ ...f, tipo_soggetto: e.target.value }))}
              className="w-full h-9 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-sm text-gray-900 dark:text-white">
              <option value="diretto">{isIt ? 'Diretto' : 'Direct'}</option>
              <option value="intermediario">{isIt ? 'Intermediario' : 'Intermediary'}</option>
            </select>
          </div>
          <FormField label="PEC" value={form.pec_address} onChange={v => setForm(f => ({ ...f, pec_address: v }))} placeholder="pec@example.it" />
          <FormField label={isIt ? 'Comune Sede' : 'City'} value={form.comune_sede} onChange={v => setForm(f => ({ ...f, comune_sede: v }))} />
          <FormField label={isIt ? 'Provincia Sede' : 'Province'} value={form.provincia_sede} onChange={v => setForm(f => ({ ...f, provincia_sede: v.toUpperCase() }))} maxLength={2} />
          <FormField label="SID Number" value={form.sid_number} onChange={v => setForm(f => ({ ...f, sid_number: v }))} />
        </div>

        {formError && (
          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <XCircle className="w-4 h-4 flex-shrink-0" /> {formError}
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={handleSubmit} disabled={isSaving}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {isIt ? 'Salva' : 'Save'}
          </button>
          <button onClick={() => { setMode('list'); setFormError(null) }}
            className="px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm">
            {isIt ? 'Annulla' : 'Cancel'}
          </button>
        </div>
      </div>
    )
  }

  // ── LIST VIEW ──
  return (
    <div className="p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          {isIt ? `Soggetti obbligati (${soggetti.length})` : `Obligated subjects (${soggetti.length})`}
        </h3>
        <button onClick={() => { setMode('create'); setForm({ ...EMPTY_SOGGETTO }); setFormError(null) }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium">
          <Plus className="w-3.5 h-3.5" /> {isIt ? 'Nuovo' : 'New'}
        </button>
      </div>

      {isLoading ? <LoadingSpinner /> : soggetti.length === 0 ? (
        <EmptyState message={isIt ? 'Nessun soggetto registrato' : 'No subjects registered'} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-gray-200 dark:border-slate-600">
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">CF</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">{isIt ? 'Nome/Denominazione' : 'Name'}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">{isIt ? 'Tipo' : 'Type'}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">PEC</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 text-right">{isIt ? 'Azioni' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {soggetti.map(s => (
                <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30">
                  <td className="px-3 py-2 font-mono text-xs">{s.codice_fiscale}</td>
                  <td className="px-3 py-2 text-xs">{s.denominazione || `${s.cognome} ${s.nome}`.trim() || '—'}</td>
                  <td className="px-3 py-2 text-xs">{s.tipo_soggetto}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{s.pec_address || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => startEdit(s)} className="p-1 text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded" title={isIt ? 'Modifica' : 'Edit'}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => {
                        if (confirm(isIt ? `Eliminare il soggetto ${s.codice_fiscale}?` : `Delete subject ${s.codice_fiscale}?`))
                          deleteMutation.mutate(s.id)
                      }} disabled={deleteMutation.isPending} className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded" title={isIt ? 'Elimina' : 'Delete'}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ============================================================
// RICEVUTE SECTION
// ============================================================

function RicevuteSection({ isIt }: { isIt: boolean }) {
  const queryClient = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [uploadMsg, setUploadMsg] = useState<string | null>(null)

  const { data: ricevute = [], isLoading } = useQuery<Ricevuta[]>({
    queryKey: ['sid-ricevute'],
    queryFn: async () => {
      const r = await api.get('/sid/ricevute')
      return r.data.data || []
    },
  })

  const uploadMutation = useMutation({
    mutationFn: async (f: File) => {
      const formData = new FormData()
      formData.append('file', f)
      const r = await api.post('/sid/ricevute/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return r.data
    },
    onSuccess: (data) => {
      if (data.success) {
        setUploadMsg(isIt ? 'Ricevuta caricata con successo' : 'Receipt uploaded successfully')
        setFile(null)
        queryClient.invalidateQueries({ queryKey: ['sid-ricevute'] })
      } else {
        setUploadMsg(data.error || 'Errore')
      }
    },
    onError: (err: unknown) => setUploadMsg(err instanceof Error ? err.message : 'Errore'),
  })

  return (
    <div className="p-5 space-y-4">
      {/* Upload area */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 dark:border-slate-600 rounded-lg text-sm cursor-pointer hover:border-indigo-400 transition-colors">
          <Upload className="w-4 h-4 text-gray-400" />
          {file ? file.name : (isIt ? 'Seleziona ricevuta...' : 'Select receipt...')}
          <input type="file" className="hidden" onChange={e => { setFile(e.target.files?.[0] || null); setUploadMsg(null) }} />
        </label>
        {file && (
          <button onClick={() => uploadMutation.mutate(file)} disabled={uploadMutation.isPending}
            className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            {uploadMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {isIt ? 'Carica' : 'Upload'}
          </button>
        )}
      </div>
      {uploadMsg && (
        <div className={`text-sm p-2 rounded-lg ${uploadMsg.includes('successo') || uploadMsg.includes('success') ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'}`}>
          {uploadMsg}
        </div>
      )}

      {/* List */}
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
        {isIt ? `Ricevute (${ricevute.length})` : `Receipts (${ricevute.length})`}
      </h3>
      {isLoading ? <LoadingSpinner /> : ricevute.length === 0 ? (
        <EmptyState message={isIt ? 'Nessuna ricevuta caricata' : 'No receipts uploaded'} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-gray-200 dark:border-slate-600">
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">{isIt ? 'Tipo' : 'Type'}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">{isIt ? 'Esito' : 'Result'}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">{isIt ? 'Protocollo' : 'Protocol'}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">{isIt ? 'File' : 'File'}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">{isIt ? 'Data' : 'Date'}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">{isIt ? 'Errori' : 'Errors'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {ricevute.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30">
                  <td className="px-3 py-2 text-xs">{r.tipo_ricevuta || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      r.esito === 'accettata' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : r.esito === 'scartata' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-400'
                    }`}>
                      {r.esito || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs font-mono">{r.protocollo_ade || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{r.filename || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.errori && r.errori.length > 0 ? (
                      <span className="text-red-500" title={r.errori.map(e => e.messaggio || e.codice).join(', ')}>
                        {r.errori.length} {isIt ? 'errori' : 'errors'}
                      </span>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ============================================================
// AUDIT SECTION
// ============================================================

function AuditSection({ isIt }: { isIt: boolean }) {
  const [offset, setOffset] = useState(0)
  const [actionFilter, setActionFilter] = useState('')
  const limit = 20

  const { data, isLoading, refetch } = useQuery<{ events: AuditEvent[]; total: number }>({
    queryKey: ['sid-audit', offset, actionFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
      if (actionFilter) params.set('action', actionFilter)
      const r = await api.get(`/sid/audit?${params}`)
      return { events: r.data.data || [], total: r.data.total || 0 }
    },
  })

  const events = data?.events || []
  const total = data?.total || 0
  const pages = Math.ceil(total / limit)
  const currentPage = Math.floor(offset / limit) + 1

  return (
    <div className="p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          {isIt ? `Audit trail (${total} eventi)` : `Audit trail (${total} events)`}
        </h3>
        <div className="flex items-center gap-2">
          <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setOffset(0) }}
            className="h-8 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-xs text-gray-700 dark:text-gray-300">
            <option value="">{isIt ? 'Tutte le azioni' : 'All actions'}</option>
            <option value="cert_upload">cert_upload</option>
            <option value="cert_delete">cert_delete</option>
            <option value="cert_activate">cert_activate</option>
            <option value="cert_test">cert_test</option>
            <option value="com_create">com_create</option>
            <option value="com_update">com_update</option>
            <option value="com_sign">com_sign</option>
            <option value="com_download">com_download</option>
            <option value="com_download_p7m">com_download_p7m</option>
            <option value="com_delete">com_delete</option>
            <option value="soggetto_create">soggetto_create</option>
            <option value="soggetto_update">soggetto_update</option>
            <option value="soggetto_delete">soggetto_delete</option>
            <option value="ricevuta_upload">ricevuta_upload</option>
            <option value="pec_lookup">pec_lookup</option>
          </select>
          <button onClick={() => refetch()} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isLoading ? <LoadingSpinner /> : events.length === 0 ? (
        <EmptyState message={isIt ? 'Nessun evento registrato' : 'No events recorded'} />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-gray-200 dark:border-slate-600">
                  <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">{isIt ? 'Data' : 'Date'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">{isIt ? 'Azione' : 'Action'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">{isIt ? 'Tipo' : 'Type'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Target</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 hidden md:table-cell">{isIt ? 'Dettaglio' : 'Detail'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {events.map(e => (
                  <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30">
                    <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                      {new Date(e.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-3 py-2"><span className="font-mono text-xs px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">{e.action}</span></td>
                    <td className="px-3 py-2 text-xs">{e.target_type}</td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-500 truncate max-w-[120px]" title={e.target_id}>{e.target_id?.slice(0, 20)}</td>
                    <td className="px-3 py-2 text-xs text-gray-400 hidden md:table-cell truncate max-w-[200px]">
                      {e.detail ? JSON.stringify(e.detail).slice(0, 60) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0}
                className="p-1.5 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-500">{currentPage} / {pages}</span>
              <button onClick={() => setOffset(offset + limit)} disabled={offset + limit >= total}
                className="p-1.5 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ============================================================
// PRIVACY SECTION
// ============================================================

function PrivacySection({ isIt }: { isIt: boolean }) {
  const [exporting, setExporting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const handleExport = async () => {
    setExporting(true)
    setResult(null)
    try {
      const r = await api.get('/sid/privacy/export')
      const blob = new Blob([JSON.stringify(r.data.data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `sid_export_${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setResult({ type: 'success', message: isIt ? 'Export completato' : 'Export completed' })
    } catch (err: unknown) {
      setResult({ type: 'error', message: err instanceof Error ? err.message : 'Errore' })
    } finally {
      setExporting(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    setDeleting(true)
    setResult(null)
    try {
      await api.delete('/sid/privacy/delete', { data: { confirm: true } })
      setResult({ type: 'success', message: isIt ? 'Tutti i dati SID sono stati cancellati. I log audit sono stati anonimizzati.' : 'All SID data deleted. Audit logs anonymized.' })
      setDeleteConfirm(false)
    } catch (err: unknown) {
      setResult({ type: 'error', message: err instanceof Error ? err.message : 'Errore' })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="p-5 space-y-5">
      <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <Lock className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800 dark:text-blue-300">
          <p className="font-medium">{isIt ? 'Privacy — GDPR Art. 15 & 17' : 'Privacy — GDPR Art. 15 & 17'}</p>
          <p className="mt-1 text-xs">
            {isIt
              ? 'Esporta o cancella tutti i dati SID del workspace. I log audit vengono mantenuti (anonimizzati) per obbligo fiscale (10 anni).'
              : 'Export or delete all SID workspace data. Audit logs are retained (anonymized) per fiscal obligation (10 years).'}
          </p>
        </div>
      </div>

      {/* Export */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          {isIt ? 'Esportazione dati (Art. 15)' : 'Data export (Art. 15)'}
        </h3>
        <button onClick={handleExport} disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {isIt ? 'Esporta tutti i dati SID' : 'Export all SID data'}
        </button>
      </div>

      {/* Delete */}
      <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-slate-700">
        <h3 className="text-sm font-semibold text-red-700 dark:text-red-400">
          {isIt ? 'Cancellazione dati (Art. 17)' : 'Data deletion (Art. 17)'}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {isIt
            ? '⚠ Operazione IRREVERSIBILE. Verranno cancellati: soggetti, comunicazioni, certificati, ricevute, configurazioni PEC. I file TXT/P7M/certificati in storage saranno rimossi.'
            : '⚠ IRREVERSIBLE operation. Will delete: subjects, communications, certificates, receipts, PEC configurations. TXT/P7M/certificate files in storage will be removed.'}
        </p>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={deleteConfirm} onChange={e => setDeleteConfirm(e.target.checked)}
            className="rounded border-gray-300 text-red-600 focus:ring-red-500" />
          {isIt ? 'Confermo la cancellazione permanente di tutti i dati SID' : 'I confirm permanent deletion of all SID data'}
        </label>
        <button onClick={handleDelete} disabled={!deleteConfirm || deleting}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
          {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          {isIt ? 'Cancella tutti i dati SID' : 'Delete all SID data'}
        </button>
      </div>

      {result && (
        <div className={`text-sm p-3 rounded-lg ${result.type === 'success' ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'}`}>
          {result.type === 'success' ? <CheckCircle2 className="w-4 h-4 inline mr-1" /> : <XCircle className="w-4 h-4 inline mr-1" />}
          {result.message}
        </div>
      )}
    </div>
  )
}

// ============================================================
// PEC SECTION
// ============================================================

interface PecConfig {
  id: string
  workspace_id: string
  soggetto_id: string
  pec_address: string
  pec_username: string
  has_password: boolean
  provider: string
  rei_accredited: boolean
  verification_status: 'not_verified' | 'verified' | 'failed'
  last_verified_at: string
  sid_number: string
  notes: string
  is_active: boolean
  created_at: string
  updated_at: string
}

const EMPTY_PEC_FORM = {
  pec_address: '',
  pec_username: '',
  pec_password: '',
  provider: '',
  soggetto_id: '',
  sid_number: '',
  rei_accredited: false,
  notes: '',
}

function PecSection({ isIt }: { isIt: boolean }) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'list' | 'create' | 'edit'>('list')
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState({ ...EMPTY_PEC_FORM })
  const [formError, setFormError] = useState<string | null>(null)

  // PEC lookup state
  const [cf, setCf] = useState('')
  const [lookupResult, setLookupResult] = useState<PecLookupResult | null>(null)
  const [lookupLoading, setLookupLoading] = useState(false)
  const [lookupError, setLookupError] = useState<string | null>(null)

  // Load configs
  const { data: configs = [], isLoading } = useQuery<PecConfig[]>({
    queryKey: ['sid-pec-configs'],
    queryFn: async () => {
      const r = await api.get('/sid/pec-config')
      return r.data.data || []
    },
  })

  // Load soggetti for linking
  const { data: soggetti = [] } = useQuery<Soggetto[]>({
    queryKey: ['sid-soggetti'],
    queryFn: async () => {
      const r = await api.get('/sid/soggetti')
      return r.data.data || []
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (data: typeof EMPTY_PEC_FORM) => {
      if (mode === 'edit' && editId) {
        const r = await api.put(`/sid/pec-config/${editId}`, data)
        return r.data
      }
      const r = await api.post('/sid/pec-config', data)
      return r.data
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['sid-pec-configs'] })
        setMode('list')
        setForm({ ...EMPTY_PEC_FORM })
        setEditId(null)
        setFormError(null)
      } else {
        setFormError(data.error || 'Errore')
      }
    },
    onError: (err: unknown) => setFormError(err instanceof Error ? err.message : 'Errore'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.delete(`/sid/pec-config/${id}`)
      return r.data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sid-pec-configs'] }),
  })

  const handleEdit = (c: PecConfig) => {
    setForm({
      pec_address: c.pec_address,
      pec_username: c.pec_username,
      pec_password: '', // never returned from backend
      provider: c.provider,
      soggetto_id: c.soggetto_id,
      sid_number: c.sid_number,
      rei_accredited: c.rei_accredited,
      notes: c.notes,
    })
    setEditId(c.id)
    setMode('edit')
    setFormError(null)
  }

  const handleSave = () => {
    if (!form.pec_address || !form.pec_address.includes('@')) {
      setFormError(isIt ? 'Indirizzo PEC obbligatorio' : 'PEC address required')
      return
    }
    saveMutation.mutate(form)
  }

  const handleLookup = async () => {
    if (!cf.trim()) return
    setLookupLoading(true)
    setLookupError(null)
    setLookupResult(null)
    try {
      const r = await api.post('/sid/pec-lookup', { codice_fiscale: cf.trim().toUpperCase() })
      if (r.data.success) {
        setLookupResult(r.data.data)
      } else {
        setLookupError(r.data.error || 'Errore')
      }
    } catch (err: unknown) {
      setLookupError(err instanceof Error ? err.message : 'Errore')
    } finally {
      setLookupLoading(false)
    }
  }

  const getSoggettoLabel = (id: string) => {
    const s = soggetti.find(x => x.id === id)
    if (!s) return '—'
    return s.denominazione || `${s.cognome} ${s.nome}`.trim() || s.codice_fiscale
  }

  if (isLoading) return <LoadingSpinner />

  // ── FORM (create / edit) ──
  if (mode === 'create' || mode === 'edit') {
    return (
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {mode === 'create'
              ? (isIt ? 'Nuova configurazione PEC' : 'New PEC configuration')
              : (isIt ? 'Modifica configurazione PEC' : 'Edit PEC configuration')}
          </h3>
          <button onClick={() => { setMode('list'); setForm({ ...EMPTY_PEC_FORM }); setEditId(null); setFormError(null) }}
            className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center gap-1">
            <ChevronLeft className="w-3 h-3" /> {isIt ? 'Indietro' : 'Back'}
          </button>
        </div>

        {formError && (
          <div className="text-sm p-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 flex items-center gap-2">
            <XCircle className="w-4 h-4 flex-shrink-0" /> {formError}
          </div>
        )}

        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormField
              label={isIt ? 'PEC accreditata *' : 'Accredited PEC *'}
              value={form.pec_address}
              onChange={v => setForm(f => ({ ...f, pec_address: v }))}
              placeholder="pec@legalmail.it"
            />
            <div>
              <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-1">
                {isIt ? 'Soggetto collegato' : 'Linked subject'}
              </label>
              <select
                value={form.soggetto_id}
                onChange={e => {
                  const sid = e.target.value
                  setForm(f => ({ ...f, soggetto_id: sid }))
                  // Auto-fill SID number from soggetto
                  const s = soggetti.find(x => x.id === sid)
                  if (s?.sid_number) {
                    setForm(f => ({ ...f, sid_number: s.sid_number }))
                  }
                }}
                className="w-full h-9 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-sm text-gray-900 dark:text-white"
              >
                <option value="">{isIt ? '— Nessuno —' : '— None —'}</option>
                {soggetti.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.denominazione || `${s.cognome} ${s.nome}`.trim()} — {s.codice_fiscale}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormField
              label={isIt ? 'Numero SID collegato' : 'Linked SID number'}
              value={form.sid_number}
              onChange={v => setForm(f => ({ ...f, sid_number: v }))}
              placeholder="SID000000"
              mono
            />
            <FormField
              label={isIt ? 'Provider PEC' : 'PEC provider'}
              value={form.provider}
              onChange={v => setForm(f => ({ ...f, provider: v }))}
              placeholder="Aruba, Legalmail, Register..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormField
              label={isIt ? 'Username PEC' : 'PEC username'}
              value={form.pec_username}
              onChange={v => setForm(f => ({ ...f, pec_username: v }))}
              placeholder={isIt ? 'Username o email di accesso' : 'Login username or email'}
            />
            <div>
              <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-1">
                {isIt ? 'Password applicativa' : 'Application password'}
              </label>
              <input
                type="password"
                value={form.pec_password}
                onChange={e => setForm(f => ({ ...f, pec_password: e.target.value }))}
                placeholder={mode === 'edit' ? (isIt ? 'Lascia vuoto per non modificare' : 'Leave blank to keep current') : '••••••••'}
                className="w-full h-9 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-sm text-gray-900 dark:text-white"
              />
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                {isIt ? 'Cifrata con AES-256 — mai visualizzata in chiaro.' : 'Encrypted with AES-256 — never displayed in plaintext.'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.rei_accredited}
                onChange={e => setForm(f => ({ ...f, rei_accredited: e.target.checked }))}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {isIt ? 'PEC accreditata REI' : 'REI accredited PEC'}
              </span>
            </label>
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-1">
              {isIt ? 'Note operative' : 'Operational notes'}
            </label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2}
              placeholder={isIt ? 'Note per l\'operatore...' : 'Notes for the operator...'}
              className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white resize-none"
            />
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={handleSave} disabled={saveMutation.isPending}
            className="px-4 h-9 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {isIt ? 'Salva' : 'Save'}
          </button>
          <button onClick={() => { setMode('list'); setForm({ ...EMPTY_PEC_FORM }); setEditId(null); setFormError(null) }}
            className="px-4 h-9 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700">
            {isIt ? 'Annulla' : 'Cancel'}
          </button>
        </div>
      </div>
    )
  }

  // ── LIST MODE ──
  return (
    <div className="p-5 space-y-4">
      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800 dark:text-blue-300">
          <p className="font-medium">{isIt ? 'PEC accreditata del soggetto / società cliente' : 'Accredited PEC of the subject / client company'}</p>
          <p className="mt-1 text-xs">
            {isIt
              ? 'Configura qui la PEC accreditata REI del soggetto obbligato per il workflow SID/AdR. In modalità Produzione le comunicazioni firmate vengono inviate automaticamente via PEC con le credenziali attive.'
              : 'Configure the REI-accredited PEC of the obligated subject for the SID/AdR workflow. In Production mode, signed communications are sent automatically via PEC using the active credentials.'}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400">
              <AlertTriangle className="w-3 h-3" />
              {isIt ? 'Workflow attivo — Invio PEC automatico' : 'Workflow active — Automatic PEC sending'}
            </span>
          </div>
        </div>
      </div>

      {/* Header + add button */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          {isIt ? 'Configurazioni PEC' : 'PEC Configurations'} ({configs.length})
        </h3>
        <button onClick={() => { setMode('create'); setForm({ ...EMPTY_PEC_FORM }); setFormError(null) }}
          className="flex items-center gap-1.5 px-3 h-8 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium">
          <Plus className="w-3.5 h-3.5" /> {isIt ? 'Aggiungi PEC' : 'Add PEC'}
        </button>
      </div>

      {/* Config cards */}
      {configs.length === 0 && (
        <EmptyState message={isIt ? 'Nessuna configurazione PEC salvata' : 'No PEC configuration saved'} />
      )}

      {configs.map(c => {
        const statusBg = c.verification_status === 'verified'
          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
          : c.verification_status === 'failed'
          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
          : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-400'
        const statusLabel = c.verification_status === 'verified'
          ? (isIt ? 'Verificata' : 'Verified')
          : c.verification_status === 'failed'
          ? (isIt ? 'Errore' : 'Failed')
          : (isIt ? 'Non verificata' : 'Not verified')

        return (
          <div key={c.id} className="p-4 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 space-y-3">
            {/* Row 1: PEC + status badges */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-mono font-medium text-gray-900 dark:text-white truncate">{c.pec_address}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {c.soggetto_id ? getSoggettoLabel(c.soggetto_id) : (isIt ? 'Nessun soggetto collegato' : 'No linked subject')}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {c.rei_accredited && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                    REI
                  </span>
                )}
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusBg}`}>
                  {statusLabel}
                </span>
              </div>
            </div>

            {/* Row 2: details grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {c.sid_number && (
                <div>
                  <span className="text-gray-400 dark:text-gray-500">SID</span>
                  <p className="font-mono text-gray-700 dark:text-gray-300">{c.sid_number}</p>
                </div>
              )}
              {c.provider && (
                <div>
                  <span className="text-gray-400 dark:text-gray-500">Provider</span>
                  <p className="text-gray-700 dark:text-gray-300">{c.provider}</p>
                </div>
              )}
              {c.pec_username && (
                <div>
                  <span className="text-gray-400 dark:text-gray-500">Username</span>
                  <p className="text-gray-700 dark:text-gray-300 truncate">{c.pec_username}</p>
                </div>
              )}
              <div>
                <span className="text-gray-400 dark:text-gray-500">{isIt ? 'Password' : 'Password'}</span>
                <p className="text-gray-700 dark:text-gray-300">{c.has_password ? '••••••••' : '—'}</p>
              </div>
              {c.last_verified_at && (
                <div>
                  <span className="text-gray-400 dark:text-gray-500">{isIt ? 'Ultima verifica' : 'Last verified'}</span>
                  <p className="text-gray-700 dark:text-gray-300">
                    {new Date(c.last_verified_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              )}
            </div>

            {/* Notes */}
            {c.notes && (
              <p className="text-xs text-gray-500 dark:text-gray-400 italic border-t border-gray-100 dark:border-slate-700 pt-2">
                {c.notes}
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <button onClick={() => handleEdit(c)}
                className="flex items-center gap-1 px-2.5 py-1 text-xs text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-md transition-colors">
                <Pencil className="w-3 h-3" /> {isIt ? 'Modifica' : 'Edit'}
              </button>
              <button onClick={() => { if (confirm(isIt ? 'Eliminare questa configurazione PEC?' : 'Delete this PEC configuration?')) deleteMutation.mutate(c.id) }}
                className="flex items-center gap-1 px-2.5 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors">
                <Trash2 className="w-3 h-3" /> {isIt ? 'Elimina' : 'Delete'}
              </button>
            </div>
          </div>
        )
      })}

      {/* ── PEC Lookup ── */}
      <div className="pt-4 border-t border-gray-200 dark:border-slate-700 space-y-2">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          {isIt ? 'Ricerca PEC del soggetto' : 'Subject PEC lookup'}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {isIt
            ? 'Verifica la PEC ufficiale di un soggetto tramite il Registro Imprese.'
            : 'Verify a subject\'s official PEC via the Business Register.'}
        </p>
        <div className="flex gap-2">
          <input type="text" value={cf} onChange={e => setCf(e.target.value.toUpperCase())} maxLength={16}
            placeholder={isIt ? 'Codice Fiscale o P.IVA' : 'Tax Code or VAT'}
            className="flex-1 h-9 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-sm font-mono text-gray-900 dark:text-white"
            onKeyDown={e => { if (e.key === 'Enter') handleLookup() }}
          />
          <button onClick={handleLookup} disabled={lookupLoading || !cf.trim()}
            className="px-4 h-9 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {isIt ? 'Cerca' : 'Search'}
          </button>
        </div>

        {lookupError && (
          <div className="text-sm p-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 flex items-center gap-2">
            <XCircle className="w-4 h-4 flex-shrink-0" /> {lookupError}
          </div>
        )}

        {lookupResult && (
          <div className={`p-4 rounded-lg border ${lookupResult.found
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-gray-50 dark:bg-slate-900/50 border-gray-200 dark:border-slate-700'
          }`}>
            {lookupResult.found ? (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-800 dark:text-green-300">{isIt ? 'PEC trovata' : 'PEC found'}</span>
                </div>
                {lookupResult.company_name && (
                  <p className="text-sm text-gray-700 dark:text-gray-300">{lookupResult.company_name}</p>
                )}
                <p className="text-sm font-mono text-gray-900 dark:text-white">{lookupResult.pec}</p>
                {/* Quick-use button */}
                <button
                  onClick={() => {
                    setForm(f => ({ ...f, pec_address: lookupResult.pec || '' }))
                    setMode('create')
                    setFormError(null)
                  }}
                  className="mt-2 flex items-center gap-1.5 px-3 h-7 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium">
                  <Plus className="w-3 h-3" /> {isIt ? 'Usa questa PEC' : 'Use this PEC'}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">{isIt ? 'Nessuna PEC trovata per questo CF/P.IVA' : 'No PEC found for this tax code'}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Readiness checklist */}
      <div className="pt-4 border-t border-gray-200 dark:border-slate-700 space-y-2">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          {isIt ? 'Stato predisposizione' : 'Readiness status'}
        </h3>
        <ReadinessItem done label={isIt ? 'Configurazione PEC accreditata del soggetto' : 'Subject accredited PEC configuration'} />
        <ReadinessItem done label={isIt ? 'Generazione comunicazioni TXT conforme' : 'Compliant TXT communication generation'} />
        <ReadinessItem done label={isIt ? 'Firma digitale sandbox' : 'Sandbox digital signature'} />
        <ReadinessItem done label={isIt ? 'Download artefatti (TXT, P7M, ZIP)' : 'Artifact download (TXT, P7M, ZIP)'} />
        <ReadinessItem done label={isIt ? 'Ricerca PEC soggetto via Registro Imprese' : 'Subject PEC lookup via Business Register'} />
        <ReadinessItem done label={isIt ? 'Invio automatico PEC' : 'Automatic PEC sending'} note={isIt ? 'Attivo in firma Produzione' : 'Active in Production signing'} />
        <ReadinessItem done={false} label={isIt ? 'Invio diretto AdE (canale SID Entratel)' : 'Direct AdE submission (SID Entratel channel)'} note={isIt ? 'Richiede accreditamento AdE' : 'Requires AdE accreditation'} />
      </div>
    </div>
  )
}

function ReadinessItem({ done, label, note }: { done: boolean; label: string; note?: string }) {
  return (
    <div className="flex items-start gap-2">
      {done
        ? <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
        : <XCircle className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0 mt-0.5" />
      }
      <div>
        <span className={`text-sm ${done ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>{label}</span>
        {note && <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-1">({note})</span>}
      </div>
    </div>
  )
}

// ============================================================
// SHARED COMPONENTS
// ============================================================

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center py-8 text-gray-400 dark:text-gray-500">
      <Eye className="w-8 h-8 mb-2 opacity-50" />
      <p className="text-sm">{message}</p>
    </div>
  )
}

function FormField({ label, value, onChange, maxLength, mono, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; maxLength?: number; mono?: boolean; placeholder?: string
}) {
  return (
    <div>
      <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-1">{label}</label>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} maxLength={maxLength} placeholder={placeholder}
        className={`w-full h-9 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-sm text-gray-900 dark:text-white ${mono ? 'font-mono' : ''}`} />
    </div>
  )
}
